import React, { useEffect, useState } from "react";
import { Box, Grid, Chip, Button, Typography, Tooltip } from "@mui/material";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import {
  getIntegrations,
  getCapabilityList,
  getDetail,
  getConfig,
  saveSchemaConfig,
  reloadSchema,
} from "./schema-manage-service";
import useApiCall from "../common/api-call";
import { useToast } from "../common/toast";
import LoadingSpinner from "../common/loading";
import SchemaConfigDialog from "./schema-config-dialog";

const EllipsisTooltipCell = ({ value, width = 150 }) => (
  <Tooltip title={value} placement="top-start">
    <Typography
      variant="body2"
      sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: width,
        display: "block",
      }}
    >
      {value}
    </Typography>
  </Tooltip>
);

const SchemaManageView = () => {
  const { apiCall } = useApiCall();
  const { errorToast, successToast } = useToast();

  const [integrations, setIntegrations] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [selectedCapability, setSelectedCapability] = useState(null);

  const [loading, setLoading] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [prodConfig, setProdConfig] = useState({ latest: "", inUse: "" });
  const [stagingConfig, setStagingConfig] = useState({ latest: "", inUse: "" });
  const [currentDialogSchema, setCurrentDialogSchema] = useState(null);
  const [details, setDetails] = useState([]);

  useEffect(() => {
    fetchIntegrations();
  }, []);

  useEffect(() => {
    if (integrations.length > 0) {
      const firstIntegration = integrations[0];
      setSelectedIntegration(firstIntegration);
      fetchCapabilities(firstIntegration.id);
    }
  }, [integrations]);

  useEffect(() => {
    setSelectedCapability(null);
  }, [capabilities]);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const data = await getIntegrations(apiCall);

      const processedIntegrations = data.schemas.map((integration, index) => ({
        id: integration.id,
        isProd: integration.prodV > 0,
        isDraft: integration.draftV > 0,
        isActive: integration.activeV > 0,
        isLatest:
          integration.draft > 0
            ? integration.activeV === integration.draftV
            : integration.activeV === integration.prodV,
      }));
      setIntegrations(processedIntegrations);
      setLoading(false);
    } catch (error) {
      errorToast("Failed to fetch integrations");
      setLoading(false);
    }
  };

  const fetchCapabilities = async integrationId => {
    try {
      const data = await getCapabilityList(apiCall, integrationId);
      const processedCapabilities = data.schemas.map((capability, index) => ({
        id: capability.id,
        isProd: capability.prodV > 0,
        isDraft: capability.draftV > 0,
        isActive: capability.activeV > 0,
        isLatest:
          capability.draft > 0
            ? capability.activeV === capability.draftV
            : capability.activeV === capability.prodV,
        integrationId: integrationId,
      }));
      setCapabilities(processedCapabilities);
    } catch (error) {
      errorToast("Failed to fetch capabilities");
    }
  };

  const handleIntegrationRowClick = params => {
    setSelectedIntegration(params.row);
    fetchCapabilities(params.row.id);
  };

  const handleCapabilityRowClick = params => {
    setSelectedCapability(params.row);
  };

  const handleSaveConfig = async envTab => {
    try {
      if (!currentDialogSchema) {
        errorToast("No configuration selected");
        return;
      }

      const configToSave =
        envTab === 0 ? prodConfig.latest : stagingConfig.latest;
      const env = envTab === 0 ? "PROD" : "STAGING";

      const resp = await saveSchemaConfig(apiCall, configToSave, env);
      if (resp && resp.saved) {
        successToast("Configuration saved successfully");
      } else {
        errorToast("Failed to save configuration");
      }

      setConfigDialogOpen(false);
    } catch (error) {
      errorToast("Failed to save configuration");
    }
  };

  const handleSaveScript = async () => {};

  const handleShowConfig = async schema => {
    try {
      const [prodDetail, stagingDetail] = await Promise.all([
        getConfig(apiCall, schema.id, "PROD"),
        getConfig(apiCall, schema.id, "STAGING"),
      ]);

      setProdConfig({
        latest: prodDetail.latest || "",
        inUse: prodDetail.inUse || "",
      });
      setStagingConfig({
        latest: stagingDetail.latest || "",
        inUse: stagingDetail.inUse || "",
      });

      setCurrentDialogSchema({ ...schema, type: "config" });
      setConfigDialogOpen(true);
    } catch (error) {
      errorToast("Failed to fetch schema details");
    }
  };

  const handleReloadSchemas = async (rebuild = false, env = "STAGING") => {
    try {
      const result = await reloadSchema(apiCall, rebuild, env);
      if (result.success) {
        successToast(
          `Schemas ${rebuild ? `rebuilt in ${env}` : "reloaded"} successfully`,
        );
        fetchIntegrations();
        fetchCapabilities(selectedIntegration?.id);
      } else {
        errorToast(
          `Failed to ${rebuild ? `rebuild in ${env}` : "reload"} schemas`,
        );
      }
    } catch (error) {
      errorToast(
        `Unexpected error during schema ${
          rebuild ? `rebuild in ${env}` : "reload"
        }`,
      );
    }
  };

  const handleShowScript = async schema => {
    try {
      const fetchedDetails = await getDetail(apiCall, schema.id);

      setDetails(fetchedDetails);
      setCurrentDialogSchema({ ...schema, type: "script" });
      setConfigDialogOpen(true);
    } catch (error) {
      errorToast("Failed to fetch schema details");
    }
  };

  const integrationsColumns = [
    {
      field: "id",
      headerName: "Integration",
      width: 150,
      renderCell: params => <EllipsisTooltipCell value={params.value} />,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: params => (
        <Box display="flex" gap={1}>
          <Chip
            label="Prod"
            color={params.row.isProd ? "success" : "default"}
            size="small"
          />
          <Chip
            label="Draft"
            color={params.row.isDraft ? "warning" : "default"}
            size="small"
          />
          <Chip
            label="Active"
            color={params.row.isActive ? "primary" : "default"}
            size="small"
          />
        </Box>
      ),
    },
    {
      field: "latest",
      headerName: "Latest",
      renderCell: params => (
        <Chip
          label={params.row.isLatest ? "Yes" : "No"}
          color={params.row.isLatest ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 160,
      sortable: false,
      renderCell: params => (
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            size="small"
            onClick={e => {
              e.stopPropagation();
              handleShowConfig(params.row);
            }}
          >
            Config
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
              handleShowScript(params.row);
            }}
          >
            Script
          </Button>
        </Box>
      ),
    },
  ];

  const capabilitiesColumns = [
    {
      field: "id",
      headerName: "Capability",
      width: 150,
      renderCell: params => <EllipsisTooltipCell value={params.value} />,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: params => (
        <Box display="flex" gap={1}>
          <Chip
            label="Prod"
            color={params.row.isProd ? "success" : "default"}
            size="small"
          />
          <Chip
            label="Draft"
            color={params.row.isDraft ? "warning" : "default"}
            size="small"
          />
          <Chip
            label="Active"
            color={params.row.isActive ? "primary" : "default"}
            size="small"
          />
        </Box>
      ),
    },
    {
      field: "latest",
      headerName: "Latest",
      renderCell: params => (
        <Chip
          label={params.row.isLatest ? "Yes" : "No"}
          color={params.row.isLatest ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      renderCell: params => (
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            size="small"
            onClick={e => {
              handleShowScript(params.row);
            }}
          >
            Script
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <LoadingSpinner />
        </Box>
      ) : (
        <Grid container direction="column">
          <Grid item>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={() => handleReloadSchemas(false)}
                >
                  Reload
                </Button>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={() => handleReloadSchemas(true, "STAGING")}
                >
                  Rebuild Staging
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleReloadSchemas(true, "PROD")}
                >
                  Rebuild Prod
                </Button>
              </Box>
            </Box>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <DataGrid
                rows={integrations}
                columns={integrationsColumns}
                onRowClick={handleIntegrationRowClick}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 20 },
                  },
                }}
                pageSizeOptions={[5, 10, 20]}
                autoHeight
                disableColumnSelector
                getRowClassName={params =>
                  params.row === selectedIntegration ? "selected-row" : ""
                }
                sx={{
                  border: "none",
                  [`& .${gridClasses.cell}`]: {
                    border: "none",
                    "&:focus": {
                      outline: "none",
                    },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                  [`& .${gridClasses.columnHeader}`]: {
                    border: "none",
                    "&:focus": {
                      outline: "none",
                    },
                  },
                  "& .selected-row": {
                    backgroundColor: "#d0d0d0 !important",
                  },
                  "& .MuiDataGrid-row:focus, & .MuiDataGrid-cell:focus": {
                    outline: "none",
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <DataGrid
                rows={capabilities}
                columns={capabilitiesColumns}
                onRowClick={handleCapabilityRowClick}
                initialState={{
                  pagination: {
                    paginationModel: { page: 0, pageSize: 20 },
                  },
                }}
                pageSizeOptions={[10, 20, 50]}
                autoHeight
                disableColumnSelector
                getRowClassName={params =>
                  params.row === selectedCapability ? "selected-row" : ""
                }
                sx={{
                  border: "none",
                  [`& .${gridClasses.cell}`]: {
                    border: "none",
                    "&:focus": {
                      outline: "none",
                    },
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                  [`& .${gridClasses.columnHeader}`]: {
                    border: "none",
                    "&:focus": {
                      outline: "none",
                    },
                  },
                  "& .selected-row": {
                    backgroundColor: "#d0d0d0 !important",
                  },
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      )}

      <SchemaConfigDialog
        open={configDialogOpen}
        onClose={() => setConfigDialogOpen(false)}
        currentSchema={currentDialogSchema}
        onSaveConfig={handleSaveConfig}
        onSaveScript={handleSaveScript}
        prodConfig={prodConfig}
        setProdConfig={setProdConfig}
        stagingConfig={stagingConfig}
        setStagingConfig={setStagingConfig}
        details={details}
        setDetails={setDetails}
      />
    </Box>
  );
};

export default SchemaManageView;
